import { createContext, useContext, useEffect, useState, type PropsWithChildren } from 'react';
import { getPlatform } from '../platform';
import { PlatformNotSupportedError, type PlatformAdapter } from '../platform/types';

export const PlatformContext = createContext<PlatformAdapter | null>(null);

type State =
  | { status: 'loading' }
  | { status: 'ready'; adapter: PlatformAdapter }
  | { status: 'unsupported'; platform: string }
  | { status: 'error'; message: string };

const RETRY_DELAYS_MS = [250, 500] as const;

export function PlatformProvider({ children }: PropsWithChildren) {
  const [state, setState] = useState<State>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    let attempt = 0;
    let unsubscribeViewport: (() => void) | null = null;

    async function bootstrap(): Promise<void> {
      try {
        const adapter = await getPlatform();
        // BUG-1210: guard against unmount-during-await window (StrictMode double-mount,
        // slow platform detection). Without this, lifecycle calls + viewport subscription
        // run on an already-unmounted component, leaking a subscription that is never
        // unsubscribed (cleanup ran before the await resolved).
        if (cancelled) return;
        // Prod guard: mock adapter has apiPlatform=null, never allow in production build
        if (import.meta.env.PROD && adapter.apiPlatform === null) {
          if (!cancelled) setState({ status: 'error', message: 'mock adapter loaded in production' });
          return;
        }
        // BUG-1210 (+ BUG-1157): strict TG lifecycle order — ready → expand →
        // disable vertical-swipe dismiss. expand() leaves compact BottomSheet so
        // content swipes scroll instead of dragging the native sheet.
        adapter.ready();
        adapter.expand();
        adapter.disableVerticalSwipes();

        // C2: pin layout to the STABLE viewport height (TG docs: never the live
        // viewportHeight). Write px into --app-height; theme.css binds html/body/#root.
        const applyAppHeight = () => {
          const h = adapter.getViewportStableHeight() ?? window.innerHeight;
          document.documentElement.style.setProperty('--app-height', `${h}px`);
        };
        applyAppHeight();

        // Refresh ONLY on stable state (isStateStable=true) — do NOT follow the
        // unstable height mid-gesture (TG docs). Idempotent: re-running bootstrap
        // (retry path) replaces a previous subscription.
        if (unsubscribeViewport) unsubscribeViewport();
        unsubscribeViewport = adapter.onViewportChanged((isStateStable) => {
          if (isStateStable) applyAppHeight();
        });

        if (!cancelled) setState({ status: 'ready', adapter });
      } catch (err) {
        if (err instanceof PlatformNotSupportedError) {
          if (!cancelled) setState({ status: 'unsupported', platform: err.platform });
          return;
        }
        if (attempt < RETRY_DELAYS_MS.length) {
          const delay = RETRY_DELAYS_MS[attempt];
          attempt++;
          setTimeout(() => { if (!cancelled) bootstrap(); }, delay);
          return;
        }
        if (!cancelled) setState({ status: 'error', message: err instanceof Error ? err.message : String(err) });
      }
    }

    bootstrap();
    return () => {
      cancelled = true;
      if (unsubscribeViewport) unsubscribeViewport();
    };
  }, []);

  if (state.status === 'loading') return <PlatformLoading />;
  if (state.status === 'unsupported') return <PlatformUnsupported platform={state.platform} />;
  if (state.status === 'error') return <PlatformError message={state.message} />;
  return <PlatformContext.Provider value={state.adapter}>{children}</PlatformContext.Provider>;
}

export function usePlatform(): PlatformAdapter {
  const ctx = useContext(PlatformContext);
  if (!ctx) throw new Error('usePlatform must be used inside PlatformProvider');
  return ctx;
}

function PlatformLoading() {
  return <div style={{ padding: 24, textAlign: 'center', color: '#666' }}>Загрузка платформы…</div>;
}

function PlatformUnsupported({ platform }: { platform: string }) {
  return (
    <div style={{ padding: 24, textAlign: 'center', maxWidth: 480, margin: '0 auto' }}>
      <h2>Платформа {platform.toUpperCase()} в разработке</h2>
      <p>Используйте Telegram-версию для участия:</p>
      <a href="https://t.me/awardybot" style={{ display: 'inline-block', marginTop: 12, padding: '10px 20px', background: '#0088cc', color: 'white', borderRadius: 8, textDecoration: 'none' }}>
        Открыть в Telegram
      </a>
    </div>
  );
}

function PlatformError({ message }: { message: string }) {
  return (
    <div style={{ padding: 24, color: '#c33' }}>
      <h2>Ошибка инициализации</h2>
      <p>{message}</p>
    </div>
  );
}
